import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Check, ChevronLeft } from 'lucide-react';
import { apiFetch } from '../utils/api';
import { useTranslation } from 'react-i18next';
import './LogSessionPage.css';

const formatTime = (secs) => {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const parseSetCount = (setsStr) => {
  const match = String(setsStr || '').match(/^(\d+)\s*[xX×]/);
  if (match) return Math.min(parseInt(match[1]), 10);
  const num = parseInt(setsStr);
  if (!isNaN(num) && num > 0) return Math.min(num, 10);
  return 3;
};

const parseCSV = (str) =>
  String(str || '').split(',').map((v) => v.trim()).filter(Boolean);

const LogSessionPage = () => {
  const pathParts = window.location.pathname.split('/');
  const workoutId = pathParts[2];
  const dayIndexLog = pathParts[4];
  const parsedDayIndex = parseInt(dayIndexLog || 0);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const [workout, setWorkout] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastSession, setLastSession] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);
  // Check por ejercicio (no por serie). Solo los ejercicios marcados se guardan.
  const [completedExercises, setCompletedExercises] = useState(new Set());

  const startTimeRef = useRef(null);
  const [elapsed, setElapsed] = useState(0);

  // v2 key: new shape {sets:[{weight,reps}]} incompatible with v1 {weight, sets:[reps]}
  const draftKey = `dragonfit_draft_v2_${workoutId}_${dayIndexLog}`;
  const timerKey = `dragonfit_timer_${workoutId}_${dayIndexLog}`;

  useEffect(() => {
    const saved = localStorage.getItem(timerKey);
    startTimeRef.current = saved ? parseInt(saved) : Date.now();
    if (!saved) localStorage.setItem(timerKey, String(startTimeRef.current));
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [timerKey]);

  useEffect(() => {
    const fetchWorkout = async () => {
      try {
        const res = await apiFetch(`/api/workouts/${workoutId}`);
        if (res.ok) {
          const data = await res.json();
          setWorkout(data);
          const dayExercises = data.days[parsedDayIndex]?.exercises || [];
          const savedDraft = localStorage.getItem(draftKey);
          if (savedDraft) {
            try {
              const draft = JSON.parse(savedDraft);
              // Validate new shape: sets must be array of objects with weight/reps
              if (Array.isArray(draft) && typeof draft[0]?.sets?.[0] === 'object') {
                setExercises(draft);
                setDraftRestored(true);
              } else {
                initExercises(dayExercises);
              }
            } catch {
              initExercises(dayExercises);
            }
          } else {
            initExercises(dayExercises);
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchWorkout();
    fetchLastSession();
  }, [workoutId, parsedDayIndex]);

  const initExercises = (exList) => {
    setExercises(
      exList.map((ex, i) => ({
        exercise_index: i,
        sets: Array.from({ length: parseSetCount(ex.sets) }, () => ({ weight: '', reps: '' })),
        notes: '',
      }))
    );
  };

  useEffect(() => {
    if (exercises.length > 0) localStorage.setItem(draftKey, JSON.stringify(exercises));
  }, [exercises, draftKey]);

  const fetchLastSession = async () => {
    try {
      const response = await apiFetch(`/api/sessions/last/${workoutId}/${dayIndexLog}`);
      if (response.ok) setLastSession(await response.json());
    } catch (error) {
      console.error('Error fetching last session:', error);
    }
  };

  // Returns per-set {weight, reps} from previous session, falling back to first set if fewer sets stored
  const getPrevSetData = (exerciseIndex) => {
    if (!lastSession?.exercises) return [];
    const prev = lastSession.exercises.find((e) => e.exercise_index === exerciseIndex);
    if (!prev) return [];
    const weights = parseCSV(prev.weight);
    const reps = parseCSV(prev.reps);
    const count = Math.max(weights.length, reps.length);
    if (count === 0) return [];
    return Array.from({ length: count }, (_, i) => ({
      weight: weights[i] ?? weights[0] ?? '',
      reps: reps[i] ?? '',
    }));
  };

  // Progress logic:
  // maxPesoAnterior = highest weight in any previous set
  // maxRepsAnterior = highest reps among previous sets that used maxPesoAnterior
  // weightDiff: max current weight - maxPesoAnterior
  // repsDiff: best reps in current sets where weight >= maxPesoAnterior, minus maxRepsAnterior
  const getProgress = (exIdx) => {
    const current = exercises[exIdx];
    const prevEx = lastSession?.exercises?.find((e) => e.exercise_index === exIdx);
    if (!prevEx || !current) return null;

    const rawPrevWeights = parseCSV(prevEx.weight).map(Number).filter((n) => !isNaN(n) && n > 0);
    const rawPrevReps = parseCSV(prevEx.reps).map(Number).filter((n) => !isNaN(n) && n > 0);
    if (rawPrevWeights.length === 0 && rawPrevReps.length === 0) return null;

    // Normalize to per-set pairs (handles single-weight legacy format)
    const pairCount = Math.max(rawPrevWeights.length, rawPrevReps.length);
    const prevPairs = Array.from({ length: pairCount }, (_, i) => ({
      weight: rawPrevWeights[i] ?? rawPrevWeights[0] ?? 0,
      reps: rawPrevReps[i] ?? 0,
    }));

    const maxPesoAnterior = Math.max(...prevPairs.map((p) => p.weight));
    const maxRepsAnterior = prevPairs
      .filter((p) => p.weight === maxPesoAnterior)
      .reduce((max, p) => Math.max(max, p.reps), 0);

    // Current filled sets
    const curSets = current.sets
      .map((s) => ({ weight: parseFloat(s.weight), reps: parseInt(s.reps) }))
      .filter((s) => !isNaN(s.weight) && s.weight > 0 && !isNaN(s.reps) && s.reps > 0);

    if (curSets.length === 0) return null;

    const maxCurrentWeight = Math.max(...curSets.map((s) => s.weight));
    const weightDiff = +(maxCurrentWeight - maxPesoAnterior).toFixed(2);

    // Reps: evaluate only sets at or above maxPesoAnterior (lower weight doesn't count)
    const setsAtOrAboveMax = curSets.filter((s) => s.weight >= maxPesoAnterior);
    let repsDiff = null;
    if (setsAtOrAboveMax.length > 0) {
      const maxRepsAtMax = Math.max(...setsAtOrAboveMax.map((s) => s.reps));
      repsDiff = maxRepsAtMax - maxRepsAnterior;
    }

    return { weightDiff, repsDiff };
  };

  const updateSet = useCallback((exIdx, setIdx, field, value) => {
    setExercises((prev) =>
      prev.map((ex, i) =>
        i === exIdx
          ? { ...ex, sets: ex.sets.map((s, j) => (j === setIdx ? { ...s, [field]: value } : s)) }
          : ex
      )
    );
  }, []);

  const updateNotes = useCallback((exIdx, value) => {
    setExercises((prev) =>
      prev.map((ex, i) => (i === exIdx ? { ...ex, notes: value } : ex))
    );
  }, []);

  const toggleExerciseDone = (exIdx) => {
    setCompletedExercises((prev) => {
      const next = new Set(prev);
      if (next.has(exIdx)) next.delete(exIdx);
      else next.add(exIdx);
      return next;
    });
  };

  const clearDraft = () => {
    localStorage.removeItem(draftKey);
    localStorage.removeItem(timerKey);
  };

  const handleSubmit = async () => {
    if (saving) return;
    // Solo se guardan los ejercicios marcados como completados.
    const payload = exercises
      .filter((ex) => completedExercises.has(ex.exercise_index))
      .map((ex) => ({
        exercise_index: ex.exercise_index,
        weight: ex.sets.map((s) => s.weight || '').join(','),
        reps: ex.sets.map((s) => s.reps || '').join(','),
        notes: ex.notes,
      }));
    if (payload.length === 0) return;
    setSaving(true);
    try {
      const response = await apiFetch('/api/sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workout_id: workout.workout_id,
          day_index: parsedDayIndex,
          duration_seconds: elapsed,
          exercises: payload,
        }),
      });
      if (response.ok) {
        clearDraft();
        navigate(`/workout/${workoutId}`);
      } else {
        setSaving(false);
      }
    } catch (error) {
      console.error(error);
      setSaving(false);
    }
  };

  const handleBack = () => setShowExitModal(true);
  const handleConfirmExit = () => { clearDraft(); navigate(-1); };
  const handleKeepDraftExit = () => { setShowExitModal(false); navigate(-1); };

  if (loading) return <div className="loading"><div className="loading-spinner" /></div>;
  if (!workout) return <div className="page-container"><p>{t('log_session.not_found')}</p></div>;

  const day = workout.days[parsedDayIndex];

  return (
    <div className="page-container animate-fade-in">
      <div className="session-header">
        <button className="workout-page-back" onClick={handleBack}>
          <ChevronLeft size={14} />
          {t('common.back')}
        </button>
        <div className="session-header-row">
          <h1 className="header-title" style={{ margin: 0 }}>{day.name}</h1>
          <div className="session-timer">
            <span className="session-timer-label">{t('log_session.time_label')}</span>
            <span className="session-timer-value">{formatTime(elapsed)}</span>
          </div>
        </div>
      </div>

      {draftRestored && (
        <div className="draft-banner">{t('log_session.draft_restored')}</div>
      )}

      {day.exercises.map((exercise, exIdx) => {
        const current = exercises[exIdx] || { sets: [], notes: '' };
        const prevEx = lastSession?.exercises?.find((e) => e.exercise_index === exIdx);
        const prevSetData = getPrevSetData(exIdx);
        const progress = getProgress(exIdx);
        const isExerciseDone = completedExercises.has(exIdx);

        const isUp = progress && (progress.weightDiff > 0 || (progress.weightDiff === 0 && progress.repsDiff > 0));
        const isDown = progress && (progress.weightDiff < 0 || (progress.weightDiff === 0 && progress.repsDiff < 0));

        return (
          <div key={exIdx} className={`exercise-log-card${isExerciseDone ? ' exercise-done' : ''}`}>

            {/* Header: name + goal badge + completion check (one per exercise) */}
            <div className="exercise-log-header">
              <div style={{ flex: 1, minWidth: 0 }}>
                <p className="exercise-log-name">{exercise.name}</p>
                {exercise.sets && (
                  <span className="exercise-log-goal">{exercise.sets}</span>
                )}
              </div>
              <button
                className={`exercise-check-btn${isExerciseDone ? ' done' : ''}`}
                onClick={() => toggleExerciseDone(exIdx)}
                type="button"
                aria-label={t('log_session.mark_exercise_done')}
                aria-pressed={isExerciseDone}
              >
                <Check size={18} />
              </button>
            </div>

            {!prevEx && (
              <p className="exercise-log-first">{t('log_session.first_time')}</p>
            )}

            {/* Column headers */}
            <div className="sets-col-header">
              <span className="sch-set" />
              <span className="sch-prev">{t('log_session.col_prev')}</span>
              <span className="sch-weight">{t('log_session.col_weight')}</span>
              <span className="sch-reps">{t('log_session.col_reps')}</span>
            </div>

            {/* Set rows — weight + reps inputs only (check moved to exercise level) */}
            <div className="sets-table">
              {current.sets.map((set, sIdx) => {
                const prev = prevSetData[sIdx] ?? prevSetData[prevSetData.length - 1];
                const prevLabel = prev
                  ? `${prev.weight ? prev.weight + 'kg' : '—'}×${prev.reps || '—'}`
                  : '—';

                return (
                  <div key={sIdx} className="set-row">
                    <span className="set-number">S{sIdx + 1}</span>
                    <span className="set-prev-data">{prevLabel}</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      className="input set-weight-input"
                      placeholder={prev?.weight || '0'}
                      value={set.weight}
                      onChange={(e) => updateSet(exIdx, sIdx, 'weight', e.target.value)}
                    />
                    <input
                      type="number"
                      inputMode="numeric"
                      className="input set-reps-input"
                      placeholder={prev?.reps || '0'}
                      value={set.reps}
                      onChange={(e) => updateSet(exIdx, sIdx, 'reps', e.target.value)}
                    />
                  </div>
                );
              })}
            </div>

            {/* ── PROGRESO VS SESIÓN ANTERIOR ─────────────────────── */}
            {progress && (
              <div className={`progress-vs-last ${isUp ? 'progress-up' : isDown ? 'progress-down' : 'progress-same'}`}>
                <span className="progress-icon">
                  {isUp ? '🔥' : isDown ? '📉' : '➡️'}
                </span>
                <div className="progress-numbers">
                  {progress.weightDiff !== null && progress.weightDiff !== 0 && (
                    <span className="progress-stat">
                      {progress.weightDiff > 0 ? `+${progress.weightDiff}` : progress.weightDiff}kg
                    </span>
                  )}
                  {progress.repsDiff !== null && progress.repsDiff !== 0 && (
                    <span className="progress-stat">
                      {progress.repsDiff > 0 ? `+${progress.repsDiff}` : progress.repsDiff} reps
                    </span>
                  )}
                  {progress.weightDiff === 0 && progress.repsDiff === 0 && (
                    <span className="progress-stat progress-same-text">{t('log_session.same_as_last')}</span>
                  )}
                </div>
                <span className="progress-label">{t('log_session.vs_last')}</span>
              </div>
            )}

            {/* Notes */}
            <input
              type="text"
              className="input mt-2"
              placeholder={t('log_session.notes_placeholder')}
              value={current.notes}
              onChange={(e) => updateNotes(exIdx, e.target.value)}
            />

            {prevEx?.notes && prevEx.notes.trim() && (
              <div className="prev-notes-card">
                <span style={{ opacity: 0.7 }}>{t('log_session.last_note')}</span>{' '}
                <span style={{ fontStyle: 'italic' }}>{prevEx.notes}</span>
              </div>
            )}
          </div>
        );
      })}

      <button
        className="btn btn-primary w-full"
        style={{ marginTop: '8px' }}
        onClick={handleSubmit}
        disabled={saving || completedExercises.size === 0}
      >
        {saving
          ? <div className="loading-spinner" style={{ width: '20px', height: '20px' }} />
          : completedExercises.size === 0
            ? t('log_session.save_btn_empty')
            : t('log_session.save_btn_count', { count: completedExercises.size })
        }
      </button>

      {showExitModal && createPortal(
        <div className="modal-overlay">
          <div className="modal" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>⚠️</div>
            <h2 style={{ marginBottom: '8px' }}>{t('log_session.exit_title')}</h2>
            <p style={{ fontSize: '14px', color: 'var(--muted-foreground)', marginBottom: '24px', lineHeight: '1.5' }}>
              {t('log_session.exit_desc')}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button className="btn btn-secondary w-full" onClick={() => setShowExitModal(false)}>
                {t('log_session.continue_training')}
              </button>
              <button
                className="btn w-full"
                style={{ color: 'var(--muted-foreground)' }}
                onClick={handleKeepDraftExit}
              >
                {t('log_session.exit_resume')}
              </button>
              <button className="btn btn-danger w-full" onClick={handleConfirmExit}>
                {t('log_session.exit_discard')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default LogSessionPage;
